import React from "react";

/**
 * CredentialRibbon
 * A single-line scrolling strip of trust credentials.
 *
 * Self-contained: no tailwind.config changes, no dependencies, no useEffect.
 * Safe for prerender — renders identically on the server and the client.
 *
 * Props
 *   items    array of strings. Defaults to Citi Cool credentials.
 *   speed    seconds for one full loop. Higher = slower. Default 32.
 *   tone     "solid" (blue background, white text) or "soft" (pale blue
 *            background, dark blue text). Default "soft".
 */

const DEFAULT_ITEMS = [
  "Authorised Daikin dealer",
  "#1 HVAC dealer, North Region — Daikin India",
  "MD Club Gold Member",
  "Serving Delhi NCR since 1990",
  "1,500+ projects completed",
  "50,000+ tonnes installed",
];

export default function CredentialRibbon({
  items = DEFAULT_ITEMS,
  speed = 32,
  tone = "soft",
}) {
  const palette =
    tone === "solid"
      ? { wrap: "bg-[#1d4ed8]", text: "text-white", rule: "bg-white/30" }
      : { wrap: "bg-[#e6f1fb]", text: "text-[#0c447c]", rule: "bg-[#0c447c]/25" };

  // One group of items. Rendered twice so the loop is seamless.
  const Group = ({ hidden }) => (
    <ul
      className="flex shrink-0 items-center"
      aria-hidden={hidden ? "true" : undefined}
    >
      {items.map((item, i) => (
        <li key={i} className="flex items-center">
          <span className="whitespace-nowrap px-5 text-[12px] leading-none tracking-[0.01em] sm:text-[13px]">
            {item}
          </span>
          <span
            className={`h-3 w-px shrink-0 ${palette.rule}`}
            aria-hidden="true"
          />
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={`cc-ribbon w-full overflow-hidden py-2.5 ${palette.wrap} ${palette.text}`}
      style={{ "--cc-ribbon-speed": `${speed}s` }}
    >
      <style>{`
        @keyframes cc-ribbon-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        .cc-ribbon-track {
          display: flex;
          width: max-content;
          animation: cc-ribbon-scroll var(--cc-ribbon-speed, 32s) linear infinite;
          will-change: transform;
        }
        .cc-ribbon:hover .cc-ribbon-track,
        .cc-ribbon:focus-within .cc-ribbon-track {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .cc-ribbon { overflow-x: auto; }
          .cc-ribbon-track { animation: none; }
        }
      `}</style>

      <div className="cc-ribbon-track">
        <Group />
        <Group hidden />
      </div>
    </div>
  );
}